import { useContext, useState, lazy, Suspense, useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { UserContext } from './providers/UserProvider';
import { Roles } from './models/Roles';
import { Login } from './pages/basic/Login';
import { PageNotFound } from './pages/basic/PageNotFound';
import { Register } from './pages/basic/Register';
import { AppUpdate } from '@capawesome/capacitor-app-update';
import { isPlatform } from './utilities/platform';
import { Loader } from './components/Loader';
import { useWindow } from './contexts/WindowContext';
import { UpdateAvailable } from './windows/UpdateAvailable';

// Lazy loading pages to reduce the initial bundle
const Tourbillon = lazy(() => import('./pages/games/Tourbillon'));
const LevelChoices = lazy(() => import('./pages/games/LevelChoices'));
const PingPong = lazy(() => import('./pages/games/PingPong'));
const Memory = lazy(() => import('./pages/games/Memory'));
const Sudok = lazy(() => import('./pages/games/Sudok'));
const Puzzle = lazy(() => import('./pages/games/Puzzle'));
const Reordon = lazy(() => import('./pages/games/Reordon'));
const MeliMots = lazy(() => import('./pages/games/MeliMots'));
const Quizz = lazy(() => import('./pages/games/Quizz'));
const Home = lazy(() => import('./pages/Home'));
const News = lazy(() => import('./pages/News'));
const Games = lazy(() => import('./pages/Games'));
const GamesTheme = lazy(() => import('./pages/GamesTheme'));
const Album = lazy(() => import('./pages/Album'));

function PatientRoutes(): JSX.Element {
    return <Switch>{/* <Route path="/home" exact component={AdminDashboard} /> */}</Switch>;
}
function FamilyRoutes(): JSX.Element {
    return <Switch>{/* <Route path="/home" exact component={AdminDashboard} /> */}</Switch>;
}
function TherapistRoutes(): JSX.Element {
    return <Switch>{/* <Route path="/home" exact component={AdminDashboard} /> */}</Switch>;
}
function AdminRoutes(): JSX.Element {
    return <Switch>{/* <Route path="/home" exact component={AdminDashboard} /> */}</Switch>;
}

function setUserRoutes(): JSX.Element {
    const userDataProp = useContext(UserContext);
    const user = userDataProp?.user;
    if (user === null) {
        document.documentElement.style.setProperty('--ion-font-family', 'Poppins-Regular');
        return <Redirect to="/login" />;
    }
    const role = user?.type;
    let mappedRole: string | undefined;
    switch (role) {
        case 'USER':
            mappedRole = Roles.SENIOR;
            break;
        case 'FAMILY':
            mappedRole = Roles.FAMILY;
            break;
        case 'PRO':
            mappedRole = Roles.PRO;
            break;
        case 'ADMIN':
            mappedRole = Roles.ADMIN;
            break;
        default:
            mappedRole = undefined;
    }
    switch (mappedRole) {
        case Roles.SENIOR:
            document.documentElement.style.setProperty('--ion-font-family', 'Poppins-Regular, sans-serif');
            return PatientRoutes();
        case Roles.FAMILY:
            document.documentElement.style.setProperty('--ion-font-family', 'Poppins-Regular');
            return FamilyRoutes();
        case Roles.PRO:
            document.documentElement.style.setProperty('--ion-font-family', 'Poppins-Regular');
            return TherapistRoutes();
        case Roles.ADMIN:
            document.documentElement.style.setProperty('--ion-font-family', 'Poppins-Regular');
            return AdminRoutes();
        default:
            document.documentElement.style.setProperty('--ion-font-family', 'Poppins-Regular');
            return <Route path="/401" component={PageNotFound} />;
    }
}

export function Router(): JSX.Element {
    const { openWindow } = useWindow();
    const [openUpdateWindow, setOpenUpdateWindow] = useState(false);

    useEffect(() => {
        if (openWindow && openUpdateWindow) {
            openWindow({
                component: () => <UpdateAvailable />,
            });
        }
    }, [openUpdateWindow]);

    const getAppUpdateInfo = async () => {
        const result = await AppUpdate.getAppUpdateInfo();
        if (
            isPlatform('android') &&
            parseInt(result.availableVersionCode ?? '') > parseInt(result.currentVersionCode)
        ) {
            setOpenUpdateWindow(true);
        }
        if (isPlatform('ios')) {
            const arrayAvailable = (result.availableVersionName ?? '').split('.');
            const arrayCurrent = result.currentVersionName.split('.');
            if (
                parseInt(arrayCurrent[0]) < parseInt(arrayAvailable[0]) ||
                (parseInt(arrayCurrent[0]) === parseInt(arrayAvailable[0]) &&
                    parseInt(arrayCurrent[1]) < parseInt(arrayAvailable[1])) ||
                (parseInt(arrayCurrent[0]) &&
                    parseInt(arrayAvailable[0]) &&
                    parseInt(arrayCurrent[1]) === parseInt(arrayAvailable[1]) &&
                    parseInt(arrayCurrent[2]) < parseInt(arrayAvailable[2]))
            ) {
                setOpenUpdateWindow(true);
            }
        }
    };

    if (isPlatform('mobile') && !isPlatform('mobileweb')) {
        getAppUpdateInfo();
    }

    return (
        <>
            <Suspense
                fallback={
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                        <Loader />
                    </div>
                }>
                <Switch>
                    <Route path="/" exact>
                        <Redirect to="/home" />
                    </Route>
                    <Route path="/home" exact component={Home} />
                    <Route path="/login" exact component={Login} />
                    <Route path="/login/:email" exact component={Login} />
                    <Route path="/register" exact component={Register} />
                    <Route path="/register/:code" exact component={Register} />
                    <Route path="/profile" exact component={Home} />
                    <Route path="/news" exact component={News} />
                    <Route path="/games" exact component={Games} />
                    <Route path="/:game/theme" exact component={GamesTheme} />
                    <Route path="/album" exact component={Album} />
                    <Route path="/:game/:id/lvl" exact component={LevelChoices} />
                    <Route path="/sudok/:id/lvl/:lvlId" exact component={Sudok} />
                    <Route path="/tourbillon/:id/lvl/:lvlId" exact component={Tourbillon} />
                    <Route path="/memory/:id/lvl/:lvlId" exact component={Memory} />
                    <Route path="/pingpong/:id/lvl/:lvlId" exact component={PingPong} />
                    <Route path="/memoriz/:id/lvl/:lvlId" exact component={Sudok} />
                    <Route path="/puzzle/:id/lvl/:lvlId" exact component={Puzzle} />
                    <Route path="/reordon/:id/lvl/:lvlId" exact component={Reordon} />
                    <Route path="/melimots/:id/lvl/:lvlId" exact component={MeliMots} />
                    <Route path="/quizz/:id/lvl/:lvlId" exact component={Quizz} />
                    {setUserRoutes()}
                    <Route path="/404" component={PageNotFound} />;
                    <Route path="*">
                        <Redirect to="/404" />
                    </Route>
                </Switch>
            </Suspense>
        </>
    );
}
